import React, { useRef, useState, useEffect } from 'react';
import { useFirestore } from '../contexts/FirestoreContext';
import { Form, Button, Col, Row, FloatingLabel } from 'react-bootstrap';

function SalesIdForm({ salesId, onSalesIdUpdated, onSalesIdAdded, onModalClose, registerCleanup }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { addData, updateData } = useFirestore();
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [fordError, setFordError] = useState('');
  const [gmError, setGmError] = useState('');

  const salesIdNbrRef = useRef();
  const salesIdNameRef = useRef();
  const fordSalesRepIdRef = useRef();
  const gmSalesRepIdRef = useRef();

  useEffect(() => {
    if (salesId) {
      if (salesIdNbrRef.current) salesIdNbrRef.current.value = salesId.strSalesIdNbr;
      if (salesIdNameRef.current) salesIdNameRef.current.value = salesId.strSalesIdName;
      if (fordSalesRepIdRef.current) fordSalesRepIdRef.current.value = salesId.strFordSalesRepId ?? '';
      if (gmSalesRepIdRef.current) gmSalesRepIdRef.current.value = salesId.strGmSalesRepId ?? '';
    }
  }, [salesId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setFordError('');
    setGmError('');

    const fordVal = fordSalesRepIdRef.current?.value.trim().toUpperCase() || '';
    const gmVal = gmSalesRepIdRef.current?.value.trim().toUpperCase() || '';

    let hasError = false;
    if (fordVal && fordVal.length !== 3 && fordVal.length !== 4) {
      setFordError('Must be empty or exactly 3 or 4 characters.');
      hasError = true;
    }
    if (gmVal && gmVal.length !== 3 && gmVal.length !== 4) {
      setGmError('Must be empty or exactly 3 or 4 characters.');
      hasError = true;
    }
    if (hasError) {
      setLoading(false);
      return;
    }

    try {
      const newSalesId = {
        strSalesIdNbr: salesIdNbrRef.current.value,
        strSalesIdName: salesIdNameRef.current.value,
        strFordSalesRepId: fordVal,
        strGmSalesRepId: gmVal
      };

      if (salesId) {
        await updateData('salesIds', salesId.id, newSalesId);
        onSalesIdUpdated(newSalesId);
      } else {
        await addData('salesIds', newSalesId);
        if (onSalesIdAdded) onSalesIdAdded(newSalesId);
      }

      if (onModalClose) onModalClose();
    } catch (err) {
      setError('Failed to save sales Id');
      console.error('Error updating/adding sales Id:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4" style={{ width: '100%', margin: '0 auto' }}>
      {error && <div className="alert alert-danger">{error}</div>}
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="salesIdNbr">
              <FloatingLabel controlId="salesIdNbr" label="Sales Id Number">
                <Form.Control type="text" ref={salesIdNbrRef} disabled={!!salesId} />
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="salesIdName">
              <FloatingLabel controlId="salesIdName" label="Sales Id Name">
                <Form.Control type="text" ref={salesIdNameRef} />
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>
        {/* New Rep ID Fields using useRef */}
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="fordSalesRepId">
              <FloatingLabel controlId="fordSalesRepId" label="Ford Sales Rep ID">
                <Form.Control
                  type="text"
                  ref={fordSalesRepIdRef}
                  isInvalid={!!fordError}
                  onChange={e => {
                    const val = e.target.value.toUpperCase();
                    if (/^[A-Z0-9]*$/.test(val) && val.length <= 4) {
                      e.target.value = val;
                      setFordError('');
                    }
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {fordError}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="gmSalesRepId">
              <FloatingLabel controlId="gmSalesRepId" label="GM Sales Rep ID">
                <Form.Control
                  type="text"
                  ref={gmSalesRepIdRef}
                  isInvalid={!!gmError}
                  onChange={e => {
                    const val = e.target.value.toUpperCase();
                    if (/^[A-Z0-9]*$/.test(val) && val.length <= 4) {
                      e.target.value = val;
                      setGmError('');
                    }
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {gmError}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>
        </Row>
        <Button variant="primary" type="submit" disabled={loading || buttonsDisabled} className="mt-3">
          {salesId ? 'Update Sales Id' : 'Add Sales Id'}
        </Button>
      </Form>
    </div>
  );
}

export default SalesIdForm;
